export const extractTokenUsage = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const raw =
    payload.token_usage ||
    payload.result?.token_usage ||
    payload.fastapi_response?.token_usage ||
    payload.body?.token_usage ||
    null;

  if (!raw || typeof raw !== "object") return null;

  return {
    query_count: Number(raw.query_count ?? raw.queryCount ?? 0) || 0,
    prompt_tokens: Number(raw.prompt_tokens ?? raw.promptTokens ?? 0) || 0,
    session_output_tokens:
      Number(raw.session_output_tokens ?? raw.sessionOutputTokens ?? 0) || 0,
    total_tokens: Number(raw.total_tokens ?? raw.totalTokens ?? 0) || 0,
    limit: Number(raw.limit ?? 0) || 0,
    blocked: raw.blocked === true || String(raw.blocked || "").toLowerCase() === "true",
  };
};

const clampPercent = (value) => Math.min(100, Math.max(0, Number(value) || 0));

export const extractUsageCycle = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const raw =
    payload.usage_cycle ||
    payload.result?.usage_cycle ||
    payload.fastapi_response?.usage_cycle ||
    payload.body?.usage_cycle ||
    null;

  if (raw && typeof raw === "object") {
    return {
      cumulative_total_tokens:
        Number(raw.cumulative_total_tokens ?? raw.cumulativeTotalTokens ?? 0) || 0,
      limit: Number(raw.limit ?? 0) || 0,
      completed_cycles:
        Number(raw.completed_cycles ?? raw.completedCycles ?? 0) || 0,
      current_cycle:
        Number(raw.current_cycle ?? raw.currentCycle ?? 1) || 1,
      current_cycle_start_tokens:
        Number(raw.current_cycle_start_tokens ?? raw.currentCycleStartTokens ?? 0) || 0,
      current_cycle_tokens:
        Number(raw.current_cycle_tokens ?? raw.currentCycleTokens ?? 0) || 0,
      current_cycle_percent: clampPercent(
        raw.current_cycle_percent ?? raw.currentCyclePercent ?? 0,
      ),
      last_completed_boundary:
        Number(raw.last_completed_boundary ?? raw.lastCompletedBoundary ?? 0) || 0,
      last_rollover_at: raw.last_rollover_at ?? raw.lastRolloverAt ?? null,
      status: String(raw.status || "active"),
    };
  }

  const tokenUsage = extractTokenUsage(payload);
  const total = Number(tokenUsage?.total_tokens ?? 0) || 0;
  const limit = Number(tokenUsage?.limit ?? 0) || 0;
  if (!(limit > 0)) return null;

  const completedCycles = Math.floor(total / limit);
  const currentCycleStartTokens = completedCycles * limit;
  const currentCycleTokens = total - currentCycleStartTokens;

  return {
    cumulative_total_tokens: total,
    limit,
    completed_cycles: completedCycles,
    current_cycle: completedCycles + 1,
    current_cycle_start_tokens: currentCycleStartTokens,
    current_cycle_tokens: currentCycleTokens,
    current_cycle_percent: clampPercent((currentCycleTokens / limit) * 100),
    last_completed_boundary: completedCycles > 0 ? completedCycles * limit : 0,
    last_rollover_at: null,
    status: "active",
  };
};

export const getUsageCycleWarningLevel = (usageCycle) => {
  const percent = Number(usageCycle?.current_cycle_percent ?? 0) || 0;
  if (percent >= 100) return "rollover";
  if (percent >= 85) return "critical";
  if (percent >= 70) return "warning";
  return "normal";
};

export const getUsageCycleRingVariant = (usageCycle) => {
  const level = getUsageCycleWarningLevel(usageCycle);
  if (level === "critical" || level === "rollover") return "danger";
  if (level === "warning") return "warn";
  return "ok";
};

export const isSessionLimitReached = (tokenUsage) => {
  if (!tokenUsage || typeof tokenUsage !== "object") return false;

  return tokenUsage.blocked === true;
};

export const tokenUsageMatches = (tokenUsageA, tokenUsageB) => {
  if (!tokenUsageA || !tokenUsageB) return false;
  return (
    Number(tokenUsageA.total_tokens || 0) === Number(tokenUsageB.total_tokens || 0) &&
    Number(tokenUsageA.limit || 0) === Number(tokenUsageB.limit || 0) &&
    Boolean(tokenUsageA.blocked) === Boolean(tokenUsageB.blocked)
  );
};

export const isSessionLimitError = (payload) => {
  if (!payload) return false;

  const checkText = (text) => {
    if (!text || typeof text !== "string") return false;
    const lower = text.toLowerCase();
    return (
      lower.includes("session budget exceeded") ||
      lower.includes("session limit reached") ||
      text.toUpperCase().includes("SESSION_LIMIT_REACHED")
    );
  };

  // Handle string payload
  if (typeof payload === "string") return checkText(payload);

  if (typeof payload !== "object") return false;

  // Check common error fields
  if (payload.error === "SESSION_LIMIT_REACHED") return true;

  const fieldsToCheck = [
    payload.message,
    payload.error,
    payload.detail,
    payload.result?.message,
    payload.fastapi_response?.message,
    payload.fastapi_response?.error,
  ];

  for (const field of fieldsToCheck) {
    if (typeof field === "string" && checkText(field)) return true;
    if (typeof field === "object" && isSessionLimitError(field)) return true;
  }

  return false;
};

export const getSessionLimitMessage = (payload) => {
  const defaultMsg = "Session limit reached. Please start a new session to continue.";
  if (!payload) return defaultMsg;

  if (typeof payload === "string") {
    return payload.toLowerCase().includes("budget")
      ? "Session limit reached. Please start a new session to continue."
      : payload;
  }

  const msg = payload.message || payload?.result?.message || payload?.error;
  if (typeof msg === "string" && msg.trim()) return msg;

  // Specific check for budget in any field
  const rawText = JSON.stringify(payload).toLowerCase();
  if (rawText.includes("budget exceeded")) {
    return "Session limit reached. Please start a new session to continue.";
  }

  return defaultMsg;
};
